import { Buffer } from 'buffer';
import {
  AccessFunction,
  AccessFunctionGridRowData,
} from 'data/accessFunction/accessFunction.types';
import { RowNode } from '@ag-grid-community/core';
import { DistributorConfiguration } from 'data/distributorConfiguration/distributorConfiguration.types';
import { Xdp } from 'data/xdp/xdp.types';
import { Network } from 'data/network/network.types';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { FormTemplateMapByKey } from 'data/template/template.types';
import { adaptInterfaceToRowData } from './accessFunctionList.adapters';
import { ExportOption } from '../containers/AccessFunctionList/components/DialogBody/ExportReportDialogBody';

const exportAfsToFile = async (
  accessFunctions: AccessFunction[],
  headers: string[],
  gridRowData: RowNode[],
  enabledAccessFunctions: SupportedFeaturesAccessFunctions,
  accessFunctionTemplates: FormTemplateMapByKey,
  networks: Network[],
  xdps: Xdp[],
  distributorConfigurationsByAccessFunctionType: {
    [key: string]: DistributorConfiguration[];
  },
  option: ExportOption,
  exportLimit: number
) => {
  const entities = accessFunctions.slice(0, exportLimit).map((af) => {
    const provisioningInterfaces = adaptInterfaceToRowData(
      'provisioningInterfaces',
      af,
      enabledAccessFunctions,
      accessFunctionTemplates,
      networks,
      xdps,
      distributorConfigurationsByAccessFunctionType
    ).map((x: AccessFunctionGridRowData) => {
      delete x.provisioningInterfaces;
      return x;
    });

    const dataInterfaces = adaptInterfaceToRowData(
      'dataInterfaces',
      af,
      enabledAccessFunctions,
      accessFunctionTemplates,
      networks,
      xdps,
      distributorConfigurationsByAccessFunctionType
    ).map((x: AccessFunctionGridRowData) => {
      delete x.provisioningInterfaces;
      return x;
    });

    const contentInterfaces = adaptInterfaceToRowData(
      'contentInterfaces',
      af,
      enabledAccessFunctions,
      accessFunctionTemplates,
      networks,
      xdps,
      distributorConfigurationsByAccessFunctionType
    ).map((x: AccessFunctionGridRowData) => {
      delete x.provisioningInterfaces;
      return x;
    });

    return {
      name: af.name,
      afType: af.type,
      tag: af.tag,
      afId: Buffer.from(af.id as string, 'base64').toString('binary') as string,
      provisioningInterfaces,
      dataInterfaces,
      contentInterfaces
    };
  });

  if (option.fileFormat === 'json') {
    exportToJsonFile(option, entities);
  }

  if (option.fileFormat === 'csv') {
    exportToCSVFile(option, accessFunctions, headers, gridRowData, entities);
  }
};

function exportToJsonFile(
  option: ExportOption,
  entities: {
    name: string;
    afType: string;
    tag: string;
    afId: string;
    provisioningInterfaces: AccessFunctionGridRowData[];
    dataInterfaces: AccessFunctionGridRowData[];
    contentInterfaces: AccessFunctionGridRowData[];
  }[]
) {
  const json = JSON.stringify(entities, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = option.filename.endsWith('.json') ? option.filename : `${option.filename}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportToCSVFile(option: ExportOption, accessFunctions: AccessFunction[], headers: string[], gridRowData: RowNode[], entities: {
  name: string;
  afType: string;
  tag: string;
  afId: string;
  provisioningInterfaces: AccessFunctionGridRowData[];
  dataInterfaces: AccessFunctionGridRowData[];
  contentInterfaces: AccessFunctionGridRowData[];
}[]) {
  if (!gridRowData.length) {
    return;
  }

  const rowEntities = entities.flatMap((e) => {
    const ret = [];
    ret.push({
      Name: e.name,
      'AF Type': e.afType,
      'AF ID': e.afId
    });
    ret.push(
      ...(e.provisioningInterfaces ?? []).map((p) => {
        return {
          Name: e.name,
          'AF Type': e.afType,
          'AF ID': e.afId,
          Status: p.state,
          'Interface Type': p.subType,
          'AF IP Address/URL': p.ipAddress,
          'IP Port': p.port,
          Destination: p.destination,
          'Configured State': p.configuredState
        };
      }),
      ...(e.dataInterfaces ?? []).map((p) => {
        return {
          Name: e.name,
          'AF Type': e.afType,
          'AF ID': e.afId,
          Status: p.state,
          'Interface Type': p.subType,
          'AF IP Address/URL': p.ipAddress,
          'IP Port': p.port,
          Destination: p.destination,
          'Configured State': p.configuredState
        };
      }),
      ...(e.contentInterfaces ?? []).map((p) => {
        return {
          Name: e.name,
          'AF Type': e.afType,
          'AF ID': e.afId,
          Status: p.state,
          'Interface Type': p.subType,
          'AF IP Address/URL': p.ipAddress,
          'IP Port': p.port,
          Destination: p.destination,
          'Configured State': p.configuredState
        };
      })
    );
    return ret;
  });

  // Prepare rows
  const rows = rowEntities.map((e: any) => {
    return headers
      .map((header) => {
        const cell = e[header] as string;
        const escaped = typeof cell === 'string' ? cell.replace(/"/g, '""') : cell;
        return `"${escaped ?? ''}"`;
      })
      .join(',');
  });

  const csvContent = [headers.join(','), ...rows].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = option.filename.endsWith('.csv') ? option.filename : `${option.filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default exportAfsToFile;
